/**
 * @fileoverview Date calculation utilities for planner module
 * @author MCHD Room Scheduler Team
 * @created 08-10-2025 16:35:00
 * 
 * Provides date range calculations for day/week/month views in hospital room scheduling.
 * Uses date-fns for reliable, immutable date operations.
 */

import { 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  addDays, 
  format,
  differenceInDays 
} from 'date-fns';

/**
 * View mode type for planner grid display
 */
export type ViewMode = 'day' | 'week' | 'month';

/**
 * Date range tuple representing start and end dates
 */
export type DateRange = [Date, Date];

/**
 * Calculate date range based on view mode and reference date
 * 
 * @param mode - View mode ('day' | 'week' | 'month')
 * @param baseDate - Reference date for calculation (defaults to today)
 * @returns Date range tuple [startDate, endDate]
 * 
 * @example
 * // Get week range for October 8, 2025 (Wednesday)
 * const [start, end] = calculateDateRange('week', new Date('2025-10-08'));
 * // Returns: [2025-10-06 (Monday), 2025-10-12 (Sunday)]
 * 
 * @created 08-10-2025 16:35:00
 */
export function calculateDateRange(
  mode: ViewMode,
  baseDate: Date = new Date()
): DateRange {
  const date = new Date(baseDate); // Clone to avoid mutation
  
  switch (mode) {
    case 'day':
      return [date, date];
      
    case 'week':
      // Start on Monday, end on Sunday (ISO week)
      return [
        startOfWeek(date, { weekStartsOn: 1 }),
        endOfWeek(date, { weekStartsOn: 1 })
      ];
      
    case 'month':
      return [
        startOfMonth(date),
        endOfMonth(date)
      ];
      
    default:
      // Fallback to day view
      return [date, date];
  }
}

/**
 * Generate array of dates for grid columns
 * Used to render multi-day planner grid (week/month views)
 * 
 * @param startDate - Range start date
 * @param endDate - Range end date
 * @returns Array of Date objects, one per day
 * 
 * @example
 * // Get all dates in a week
 * const dates = getDateColumns(
 *   new Date('2025-10-06'),
 *   new Date('2025-10-12')
 * );
 * // Returns: [2025-10-06, 2025-10-07, ..., 2025-10-12] (7 dates)
 * 
 * @created 08-10-2025 16:35:00
 */
export function getDateColumns(startDate: Date, endDate: Date): Date[] {
  const dates: Date[] = [];
  let current = new Date(startDate);
  
  // Generate dates day by day
  while (current <= endDate) {
    dates.push(new Date(current));
    current = addDays(current, 1);
  }
  
  return dates;
}

/**
 * Validate date range is within acceptable limits
 * Prevents excessive API queries and performance issues
 * 
 * @param startDate - Range start date
 * @param endDate - Range end date
 * @param maxDays - Maximum allowed days (default: 90)
 * @returns Validation result with error message if invalid
 * 
 * @example
 * const validation = validateDateRange(start, end);
 * if (!validation.isValid) {
 *   notifications.show({ message: validation.error, color: 'red' });
 * }
 * 
 * @created 08-10-2025 16:35:00
 */
export function validateDateRange(
  startDate: Date,
  endDate: Date,
  maxDays: number = 90
): { isValid: boolean; error?: string } {
  // Check end date is after start date
  if (endDate < startDate) {
    return {
      isValid: false,
      error: 'End date must be after start date'
    };
  }
  
  // Check range is not too large
  const daysDiff = differenceInDays(endDate, startDate);
  if (daysDiff > maxDays) {
    return {
      isValid: false,
      error: `Date range too large. Maximum ${maxDays} days allowed. Please select a smaller range.`
    };
  }
  
  return { isValid: true };
}

/**
 * Format date range for display in UI
 * 
 * @param startDate - Range start date
 * @param endDate - Range end date
 * @param viewMode - Current view mode (affects format)
 * @returns Formatted string for display
 * 
 * @example
 * formatDateRangeDisplay(new Date('2025-10-06'), new Date('2025-10-12'), 'week')
 * // Returns: "Oct 6 - Oct 12, 2025"
 * 
 * @created 08-10-2025 16:35:00
 */
export function formatDateRangeDisplay(
  startDate: Date,
  endDate: Date,
  viewMode: ViewMode
): string {
  if (viewMode === 'day') {
    return format(startDate, 'MMMM d, yyyy');
  }
  
  // Check if same month
  const sameMonth = startDate.getMonth() === endDate.getMonth();
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  
  if (sameMonth && sameYear) {
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'd, yyyy')}`;
  } else if (sameYear) {
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
  } else {
    return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
  }
}

/**
 * Calculate next/previous date range based on view mode
 * Used for navigation arrows (Previous Week / Next Week)
 * 
 * @param currentRange - Current date range
 * @param direction - Navigation direction ('next' | 'prev')
 * @param viewMode - View mode to determine range size
 * @returns New date range
 * 
 * @example
 * const [start, end] = calculateDateRange('week', new Date('2025-10-08'));
 * const nextWeek = navigateDateRange([start, end], 'next', 'week');
 * // Returns: [2025-10-13, 2025-10-19]
 * 
 * @created 08-10-2025 16:35:00
 */
export function navigateDateRange(
  currentRange: DateRange,
  direction: 'next' | 'prev',
  viewMode: ViewMode
): DateRange {
  const [start, end] = currentRange;
  const days = differenceInDays(end, start) + 1; // +1 to include both dates
  
  if (direction === 'next') {
    const newStart = addDays(start, days);
    return calculateDateRange(viewMode, newStart);
  } else {
    const newStart = addDays(start, -days);
    return calculateDateRange(viewMode, newStart);
  }
}

